import React, { useState } from 'react';
import {
  X,
  Power,
  AlertTriangle,
  Shield,
  Activity,
  Clock,
  FileX,
  FolderX,
  Trash2
} from 'lucide-react';
import { useKillSwitch } from '../hooks/useKillSwitch';

interface KillSwitchModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedFileIds?: string[];
  onRefresh?: () => void;
}

export const KillSwitchModal: React.FC<KillSwitchModalProps> = ({
  isOpen,
  onClose,
  selectedFileIds = [],
  onRefresh
}) => {
  const { status, logs, loading, toggling, handleToggleGlobal, handleRemoveResource } = useKillSwitch(isOpen, onRefresh);
  const [activeTab, setActiveTab] = useState<'status' | 'logs'>('status');
  const [globalReason, setGlobalReason] = useState('');

  const onToggleGlobal = async () => {
    const ok = await handleToggleGlobal(globalReason);
    if (ok) setGlobalReason('');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-red-500 to-red-600">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8 text-white" />
              <div>
                <h2 className="text-2xl font-bold text-white">Kill Switch Control</h2>
                <p className="text-red-100 text-sm">Emergency service control panel</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-red-700 rounded-lg transition-colors"
            >
              <X className="w-6 h-6 text-white" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 bg-gray-50">
          <div className="flex">
            <button
              onClick={() => setActiveTab('status')}
              className={`flex-1 px-6 py-3 font-medium transition-colors ${
                activeTab === 'status'
                  ? 'bg-white text-red-600 border-b-2 border-red-600'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <Power className="w-4 h-4" />
                Status & Control
              </div>
            </button>
            <button
              onClick={() => setActiveTab('logs')}
              className={`flex-1 px-6 py-3 font-medium transition-colors ${
                activeTab === 'logs'
                  ? 'bg-white text-red-600 border-b-2 border-red-600'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <Activity className="w-4 h-4" />
                Activity Logs
              </div>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
            </div>
          ) : activeTab === 'status' ? (
            <div className="space-y-6">
              {/* Global Kill Switch */}
              <div className={`p-6 rounded-lg border-2 ${
                status?.global.active 
                  ? 'bg-red-50 border-red-300' 
                  : 'bg-green-50 border-green-300'
              }`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-3 rounded-full ${
                      status?.global.active ? 'bg-red-100' : 'bg-green-100'
                    }`}>
                      <Power className={`w-6 h-6 ${
                        status?.global.active ? 'text-red-600' : 'text-green-600'
                      }`} />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">Global Kill Switch</h3>
                      <p className={`text-sm font-medium ${
                        status?.global.active ? 'text-red-600' : 'text-green-600'
                      }`}>
                        {status?.global.active ? '🔴 ACTIVE - Service Blocked' : '🟢 INACTIVE - Service Running'}
                      </p>
                    </div>
                  </div>
                  {status?.global.active && (
                    <div className="flex items-center gap-2 text-red-600">
                      <AlertTriangle className="w-5 h-5 animate-pulse" />
                      <span className="font-bold">EMERGENCY MODE</span>
                    </div>
                  )}
                </div>

                {status?.global.active && status.global.reason && (
                  <div className="mb-4 p-3 bg-white rounded border border-red-200">
                    <p className="text-sm font-medium text-gray-700">Reason:</p>
                    <p className="text-gray-900">{status.global.reason}</p>
                  </div>
                )}

                {!status?.global.active && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Reason for Activation (Required)
                    </label>
                    <input
                      type="text"
                      value={globalReason}
                      onChange={(e) => setGlobalReason(e.target.value)}
                      placeholder="e.g., Emergency maintenance, Security incident, etc."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  </div>
                )}

                <button
                  onClick={onToggleGlobal}
                  disabled={toggling}
                  className={`w-full py-3 px-4 rounded-lg font-bold text-white transition-all ${
                    status?.global.active
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-red-600 hover:bg-red-700'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {toggling ? 'Processing...' : status?.global.active ? '🟢 DEACTIVATE Kill Switch' : '🔴 ACTIVATE Kill Switch'}
                </button>

                {status?.global.updated && (
                  <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
                    <Clock className="w-3 h-3" />
                    Last updated: {formatDate(status.global.updated)}
                  </div>
                )}
              </div>

              {/* Blocked Resources */}
              {selectedFileIds.length > 0 && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Activity className="w-4 h-4 text-blue-600" />
                    <p className="text-sm font-medium text-blue-900">
                      {selectedFileIds.length} file(s) selected
                    </p>
                  </div>
                  <p className="text-xs text-blue-700">
                    Use the resource blocking feature below to block specific files or folders.
                  </p>
                </div>
              )}

              {/* Blocked Resources */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Blocked Files */}
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-2 mb-3">
                    <FileX className="w-5 h-5 text-red-600" />
                    <h4 className="font-bold text-gray-900">Blocked Files</h4>
                    <span className="ml-auto bg-red-100 text-red-600 px-2 py-1 rounded-full text-xs font-bold">
                      {status?.files.length || 0}
                    </span>
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {status?.files.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-4">No blocked files</p>
                    ) : (
                      status?.files.map((file) => (
                        <div key={file.id} className="p-2 bg-white rounded border border-gray-200 text-sm">
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-900 truncate">{file.resource_id}</p>
                              {file.reason && (
                                <p className="text-xs text-gray-500 truncate">{file.reason}</p>
                              )}
                            </div>
                            <button
                              onClick={() => handleRemoveResource(file.id)}
                              className="ml-2 p-1 hover:bg-red-50 rounded text-red-600"
                              title="Remove block"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Blocked Folders */}
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-2 mb-3">
                    <FolderX className="w-5 h-5 text-red-600" />
                    <h4 className="font-bold text-gray-900">Blocked Folders</h4>
                    <span className="ml-auto bg-red-100 text-red-600 px-2 py-1 rounded-full text-xs font-bold">
                      {status?.folders.length || 0}
                    </span>
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {status?.folders.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-4">No blocked folders</p>
                    ) : (
                      status?.folders.map((folder) => (
                        <div key={folder.id} className="p-2 bg-white rounded border border-gray-200 text-sm">
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-900 truncate">{folder.resource_id}</p>
                              {folder.reason && (
                                <p className="text-xs text-gray-500 truncate">{folder.reason}</p>
                              )}
                            </div>
                            <button
                              onClick={() => handleRemoveResource(folder.id)}
                              className="ml-2 p-1 hover:bg-red-50 rounded text-red-600"
                              title="Remove block"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>

              {/* Warning */}
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-bold text-yellow-900 mb-1">Warning</p>
                    <p className="text-yellow-800">
                      When the global kill switch is active, all API endpoints will be blocked except for authentication and kill switch management. 
                      Use this only in emergency situations.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Logs Tab */
            <div className="space-y-2">
              {logs.length === 0 ? (
                <div className="text-center py-12">
                  <Activity className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No activity logs yet</p>
                </div>
              ) : (
                logs.map((log) => (
                  <div key={log.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                            log.action.includes('activated') 
                              ? 'bg-red-100 text-red-600'
                              : log.action.includes('deactivated')
                              ? 'bg-green-100 text-green-600'
                              : 'bg-blue-100 text-blue-600'
                          }`}>
                            {log.action}
                          </span>
                        </div>
                        {log.details && (
                          <p className="text-sm text-gray-600 mt-2">
                            {typeof log.details === 'string' ? log.details : JSON.stringify(JSON.parse(log.details), null, 2)}
                          </p>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 ml-4 flex-shrink-0">
                        {formatDate(log.created_at)}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};